import {
  validateTwinTowerCombineConfig,
  type TwinTowerCombineConfig,
  type TwinTowerId,
} from './TwinTowerCombineConfig.js';

export type TwinTowerTransferResult = 'accepted' | 'complete' | 'invalid' | 'empty-source';

export interface TwinTowerCombineSnapshot {
  readonly towerA: number;
  readonly towerB: number;
  readonly total: number;
  readonly isComplete: boolean;
  readonly recomposedAddends: readonly [number, number];
}

export class TwinTowerCombineState {
  private towerA: number;
  private towerB: number;

  constructor(readonly config: TwinTowerCombineConfig) {
    validateTwinTowerCombineConfig(config);
    this.towerA = config.addendA;
    this.towerB = config.addendB;
  }

  transfer(from: TwinTowerId, amount = 1): TwinTowerTransferResult {
    if (!Number.isInteger(amount) || amount <= 0 || this.isComplete) return 'invalid';
    const source = from === 'a' ? this.towerA : this.towerB;
    if (source < amount) return 'empty-source';

    if (from === 'a') {
      this.towerA -= amount;
      this.towerB += amount;
    } else {
      this.towerB -= amount;
      this.towerA += amount;
    }
    return this.isComplete ? 'complete' : 'accepted';
  }

  reset() {
    this.towerA = this.config.addendA;
    this.towerB = this.config.addendB;
  }

  get isComplete() {
    const destination = this.config.composeInto === 'a' ? this.towerA : this.towerB;
    return destination === this.config.landmark && this.total === this.config.target;
  }

  get total() { return this.towerA + this.towerB; }

  get snapshot(): TwinTowerCombineSnapshot {
    const recomposedAddends: [number, number] = this.config.composeInto === 'a'
      ? [this.towerA, this.towerB]
      : [this.towerB, this.towerA];
    return {
      towerA: this.towerA,
      towerB: this.towerB,
      total: this.total,
      isComplete: this.isComplete,
      recomposedAddends,
    };
  }
}
